import React from 'react';
import {apiFetch} from '../../api/apiClient';
import {useAuth} from '../../context/AuthContext';
import {usePageIdentity} from '../Identity/BrowserIdentity';
type Runtime={id:string;name:string;launchPath:string;iconPath?:string;script:string;style?:string;apiBasePath?:string;hostApiVersion:1};
type Module={mount:(element:HTMLElement,context:{hostApiVersion:1;application:Runtime;user:{id:string;displayName:string;role:string}})=>(()=>void)|void};
export default function InstalledApp({fallback}:{fallback:React.ReactElement}):React.ReactElement{
 const {user}=useAuth(),element=React.useRef<HTMLDivElement>(null);
 const [result,setResult]=React.useState<{key:string;app:Runtime|null}|null>(null),[error,setError]=React.useState('');
 const key=window.location.pathname+'|'+user?.id+'|'+user?.role;
 const app=result?.key===key?result.app:undefined;
 usePageIdentity(app?.name,error?'':app?.iconPath);
 React.useEffect(()=>{
  let active=true;setError('');setResult(null);
  apiFetch('/api/installed-apps/resolve?path='+encodeURIComponent(window.location.pathname)).then(async response=>{
   if(!response.ok)throw new Error(response.status===403?'Administrator access is required.':'The application could not be loaded.');
   const value=await response.json() as Runtime|null;if(active)setResult({key,app:value});
  }).catch(e=>{if(active)setError(e instanceof Error?e.message:'The application could not be loaded.');});
  return()=>{active=false;};
 },[key]);
 React.useEffect(()=>{
  if(!app||!user||!element.current)return;
  let active=true,unmount:(()=>void)|void;let link:HTMLLinkElement|undefined;
  const node=element.current;
  const timeout=window.setTimeout(()=>{if(active){active=false;link?.remove();setError('The application took too long to load. Refresh to try again.');}},15000);
  async function load(){
   if(app!.style){
    link=document.createElement('link');link.rel='stylesheet';link.href=app!.style;
    await new Promise<void>((resolve,reject)=>{link!.onload=()=>resolve();link!.onerror=()=>reject(new Error('Application styles could not be loaded.'));document.head.appendChild(link!);});
   }
   const module=await import(/* webpackIgnore: true */ app!.script) as Module;
   if(!active)return;
   if(typeof module.mount!=='function')throw new Error('The application package has no supported entry point.');
   unmount=module.mount(node,{hostApiVersion:1,application:app!,user:{id:user!.id,displayName:user!.displayName||user!.username,role:user!.role}});
   window.clearTimeout(timeout);
  }
  void load().catch(e=>{if(active){setError(e instanceof Error?e.message:'The application could not start.');window.clearTimeout(timeout);}});
  return()=>{active=false;window.clearTimeout(timeout);if(typeof unmount==='function')unmount();link?.remove();};
 },[app,user]);
 if(error)return <main style={{padding:32}}><h1>Application unavailable</h1><p>{error}</p><a href="/">Return to Conductor</a></main>;
 if(app===null)return fallback;
 if(!app)return <p style={{padding:32}}>Loading application…</p>;
 return <div ref={element}/>;
}
